export interface LandingMetadata {
  title: string
  description: string
  language: string
  slug: string
}

export interface LandingContent {
  nav: {
    howItHelps: string
    steps: string
    stories: string
    healthEncyclopedia: string
    pricing: string
    contact: string
  }
  hero: {
    badge: string
    headline: string
    description: string
    ctaPrimary: string
    ctaSecondary: string
    bullets: string[]
    medicalKnowledgeTitle: string
    medicalKnowledgeDescription: string
    chatUserMessage: string
    chatAiResponse: string
    chatAnxietyEasing: string
    chatHotlineReady: string
    chatMemoryUpdate: string
    stats: Array<{ value: string; label: string }>
  }
  howItHelps: {
    badge: string
    title: string
    cards: Array<{ title: string; description: string }>
  }
  steps: {
    subtitle: string
    title: string
    ctaButton: string
    items: Array<{ title: string; description: string }>
  }
  dailySupport: {
    badge: string
    title: string
    description: string
    points: Array<{ title: string; description: string }>
  }
  trust: {
    title: string
    description: string
    points: string[]
  }
  stories: {
    title: string
    testimonials: Array<{ quote: string; author: string }>
  }
  pricing: {
    badge: string
    title: string
    plans: Array<{
      name: string
      price: string
      priceCaption: string
      description: string
      stats: string
      badge: string
      features: string[]
      cta: string
    }>
  }
  contact: {
    badge: string
    title: string
    description: string
    ctaPrimary: string
    ctaSecondary: string
    cardTitle: string
    cardDescription: string
    careTeam: string
    partnerships: string
    secureUploads: string
  }
}

export interface ParsedLanding {
  metadata: LandingMetadata
  content: LandingContent
  rawContent: string
  htmlContent: string
}

type FrontmatterMap = Record<string, unknown>

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

// Parse frontmatter from markdown (EXACT COPY from markdown.ts)
function parseFrontmatter(markdown: string): { metadata: FrontmatterMap; content: string } {
  try {
    const frontmatterRegex = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/
    const match = markdown.match(frontmatterRegex)

    if (!match) {
      console.error('No frontmatter found in markdown')
      return { metadata: {}, content: markdown }
    }

    const frontmatterText = match[1]
    const content = match[2]
    const metadata: FrontmatterMap = {}

    const lines = frontmatterText.split(/\r?\n/)
    let currentKey = ''
    let currentValue = ''

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      const colonIndex = line.indexOf(':')

      if (colonIndex > 0 && !line.startsWith(' ') && !line.startsWith('\t')) {
        if (currentKey) {
        metadata[currentKey] = parseValue(currentValue.trim())
        }
        currentKey = line.substring(0, colonIndex).trim()
        currentValue = line.substring(colonIndex + 1).trim()
      } else if (currentKey) {
        currentValue += ' ' + line.trim()
      }
    }

    if (currentKey) {
    metadata[currentKey] = parseValue(currentValue.trim())
    }

    return { metadata, content }
  } catch (error) {
    console.error('Error parsing frontmatter:', error)
    return { metadata: {}, content: markdown }
  }
}

function parseValue(value: string): unknown {
  if (!value) return ''

  if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
    return value.slice(1, -1)
  }

  if (value.startsWith('[') && value.endsWith(']')) {
    return value
      .slice(1, -1)
      .split(',')
      .map(item => item.trim().replace(/^["']|["']$/g, ''))
  }

  return value
}

// Simple markdown to HTML converter (EXACT COPY from markdown.ts)
function markdownToHtml(markdown: string): string {
  let html = markdown

  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>')
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>')
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>')

  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')

  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
  html = html.replace(/^---$/gm, '<hr>')

  const lines = html.split(/\r?\n/)
  const processedLines: string[] = []
  let inList = false

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    if (!trimmed) {
      if (inList) {
        processedLines.push('</ul>')
        inList = false
      }
      processedLines.push('')
      continue
    }

    if (trimmed.startsWith('<')) {
      if (inList && !trimmed.startsWith('<li>')) {
        processedLines.push('</ul>')
        inList = false
      }
      processedLines.push(line)
      continue
    }

    if (trimmed.startsWith('- ')) {
      if (!inList) {
        processedLines.push('<ul>')
        inList = true
      }
      processedLines.push(`<li>${trimmed.substring(2)}</li>`)
      continue
    }

    if (inList) {
      processedLines.push('</ul>')
      inList = false
    }

    processedLines.push(`<p>${line}</p>`)
  }

  if (inList) {
    processedLines.push('</ul>')
  }

  html = processedLines.join('\n')

  return html
}

type PlanAccumulator = {
  name?: string
  price?: string
  priceCaption?: string
  description?: string
  stats?: string
  badge?: string
  features: string[]
  cta?: string
}

// Parse markdown content into structured LandingContent object
function parseMarkdownContent(markdown: string): LandingContent {
  const lines = markdown.split('\n')

  // Helper to extract list items after a heading
  const extractList = (startIndex: number): string[] => {
    const items: string[] = []
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue
      if (line.startsWith('##') || line.startsWith('###') || line.startsWith('####')) break
      if (line.startsWith('- ')) {
        items.push(line.substring(2).trim())
      }
    }
    return items
  }

  // Helper to extract text after a heading
  const extractText = (startIndex: number): string => {
    let text = ''
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue
      if (line.startsWith('##') || line.startsWith('###') || line.startsWith('####') || line.startsWith('**')) break
      text = line
      break
    }
    return text
  }

  // Parse navigation
  const navIndex = lines.findIndex(l => l.trim() === '## Navigation')
  const navItems = navIndex >= 0 ? extractList(navIndex + 1) : []

  // Parse hero section
  const heroIndex = lines.findIndex(l => l.trim() === '## Hero Section')
  const badgeIndex = lines.findIndex((l, i) => i > heroIndex && l.trim() === '### Badge')
  const headlineIndex = lines.findIndex((l, i) => i > heroIndex && l.trim() === '### Headline')
  const descIndex = lines.findIndex((l, i) => i > heroIndex && l.trim() === '### Description')
  const ctaIndex = lines.findIndex((l, i) => i > heroIndex && l.trim() === '### CTA Buttons')
  const bulletsIndex = lines.findIndex((l, i) => i > heroIndex && l.trim() === '### Hero Bullets')
  const medKnowIndex = lines.findIndex((l, i) => i > heroIndex && l.trim() === '### Medical Knowledge Box')
  const medTitleIndex = lines.findIndex((l, i) => i > medKnowIndex && l.trim() === '#### Title')
  const medDescIndex = lines.findIndex((l, i) => i > medKnowIndex && l.trim() === '#### Description')
  const chatDemoIndex = lines.findIndex((l, i) => i > heroIndex && l.trim() === '### Live Chat Demo')
  const userMsgIndex = lines.findIndex((l, i) => i > chatDemoIndex && l.trim() === '#### User Message')
  const aiRespIndex = lines.findIndex((l, i) => i > chatDemoIndex && l.trim() === '#### Diagnoza Response')
  const tagsIndex = lines.findIndex((l, i) => i > chatDemoIndex && l.trim().startsWith('**Tags:**'))
  const memUpdateIndex = lines.findIndex((l, i) => i > chatDemoIndex && l.trim() === '#### Memory Update')
  const statsIndex = lines.findIndex((l, i) => i > heroIndex && l.trim() === '### Stats')

  const ctaButtons = ctaIndex >= 0 ? extractList(ctaIndex + 1) : []
  const statsLines = statsIndex >= 0 ? extractList(statsIndex + 1) : []
  const chatTags = tagsIndex >= 0 ? extractList(tagsIndex + 1) : []

  // Parse stats
  const stats = statsLines.map(line => {
    const [value, ...labelParts] = line.split(' - ')
    return { value: value.trim(), label: labelParts.join(' - ').trim() }
  })

  // Parse How It Helps section
  const howIndex = lines.findIndex(l => l.trim() === '## How It Helps Section')
  const howBadgeIndex = lines.findIndex((l, i) => i > howIndex && l.trim() === '### Section Badge')
  const howTitleIndex = lines.findIndex((l, i) => i > howIndex && l.trim() === '### Section Title')
  const howCardsIndex = lines.findIndex((l, i) => i > howIndex && l.trim() === '### Support Cards')

  // Parse cards - find all #### headers after Support Cards
  const cards: Array<{ title: string; description: string }> = []
  if (howCardsIndex >= 0) {
    for (let i = howCardsIndex + 1; i < lines.length; i++) {
      if (lines[i].trim().startsWith('## ')) break // Next section
      if (lines[i].trim().startsWith('#### ')) {
        const title = lines[i].trim().substring(5)
        const description = extractText(i + 1)
        cards.push({ title, description })
      }
    }
  }

  // Parse Steps section
  const stepsIndex = lines.findIndex(l => l.trim() === '## Steps Section')
  const stepsSubtitleIndex = lines.findIndex((l, i) => i > stepsIndex && l.trim() === '### Section Subtitle')
  const stepsTitleIndex = lines.findIndex((l, i) => i > stepsIndex && l.trim() === '### Section Title')
  const stepsCtaIndex = lines.findIndex((l, i) => i > stepsIndex && l.trim() === '### CTA Button')
  const stepsItemsIndex = lines.findIndex((l, i) => i > stepsIndex && l.trim() === '### Steps')

  const stepItems: Array<{ title: string; description: string }> = []
  if (stepsItemsIndex >= 0) {
    for (let i = stepsItemsIndex + 1; i < lines.length; i++) {
      if (lines[i].trim().startsWith('## ')) break
      if (lines[i].trim().startsWith('#### ')) {
        const titleMatch = lines[i].trim().match(/^####\s+Step\s+\d+:\s+(.+)/)
        const title = titleMatch ? titleMatch[1] : lines[i].trim().substring(5)
        const description = extractText(i + 1)
        stepItems.push({ title, description })
      }
    }
  }

  // Parse Daily Support
  const dailyIndex = lines.findIndex(l => l.trim() === '## Daily Support Section')
  const dailyBadgeIndex = lines.findIndex((l, i) => i > dailyIndex && l.trim() === '### Badge')
  const dailyTitleIndex = lines.findIndex((l, i) => i > dailyIndex && l.trim() === '### Title')
  const dailyDescIndex = lines.findIndex((l, i) => i > dailyIndex && l.trim() === '### Description')
  const dailyPointsIndex = lines.findIndex((l, i) => i > dailyIndex && l.trim() === '### Support Points')

  const dailyPoints: Array<{ title: string; description: string }> = []
  if (dailyPointsIndex >= 0) {
    for (let i = dailyPointsIndex + 1; i < lines.length; i++) {
      if (lines[i].trim().startsWith('## ')) break
      if (lines[i].trim().startsWith('#### ')) {
        const title = lines[i].trim().substring(5)
        const description = extractText(i + 1)
        dailyPoints.push({ title, description })
      }
    }
  }

  // Parse Trust section
  const trustIndex = lines.findIndex(l => l.trim() === '## Trust and Safety Section')
  const trustTitleIndex = lines.findIndex((l, i) => i > trustIndex && l.trim() === '### Title')
  const trustDescIndex = lines.findIndex((l, i) => i > trustIndex && l.trim() === '### Description')
  const trustPointsIndex = lines.findIndex((l, i) => i > trustIndex && l.trim() === '### Trust Points')

  // Parse Stories section
  const storiesIndex = lines.findIndex(l => l.trim() === '## Stories Section')
  const storiesTitleIndex = lines.findIndex((l, i) => i > storiesIndex && l.trim() === '### Section Title')
  const testimonialsIndex = lines.findIndex((l, i) => i > storiesIndex && l.trim() === '### Testimonials')

  const testimonials: Array<{ quote: string; author: string }> = []
  if (testimonialsIndex >= 0) {
    for (let i = testimonialsIndex + 1; i < lines.length; i++) {
      if (lines[i].trim().startsWith('## ')) break
      if (lines[i].trim().startsWith('#### ')) {
        const author = lines[i].trim().substring(5)
        let quote = ''
        for (let j = i + 1; j < lines.length; j++) {
          if (lines[j].trim().startsWith('####') || lines[j].trim().startsWith('##')) break
          if (lines[j].trim()) {
            quote = lines[j].trim()
            break
          }
        }
        testimonials.push({ quote, author })
      }
    }
  }

  // Parse Pricing section
  const pricingIndex = lines.findIndex(l => l.trim() === '## Pricing Section')
  const pricingBadgeIndex = lines.findIndex((l, i) => i > pricingIndex && l.trim() === '### Section Badge')
  const pricingTitleIndex = lines.findIndex((l, i) => i > pricingIndex && l.trim() === '### Section Title')
  const plansIndex = lines.findIndex((l, i) => i > pricingIndex && l.trim() === '### Plans')

  const plans: Array<{
    name: string
    price: string
    priceCaption: string
    description: string
    stats: string
    badge: string
    features: string[]
    cta: string
  }> = []

  if (plansIndex >= 0) {
    let currentPlan: PlanAccumulator | null = null
    for (let i = plansIndex + 1; i < lines.length; i++) {
      const line = lines[i].trim()
      if (line.startsWith('## ')) break
      if (line.startsWith('#### ') && line.includes('Plan')) {
        if (currentPlan) plans.push(currentPlan as LandingContent['pricing']['plans'][number])
        currentPlan = { features: [] }
        currentPlan.name = line.substring(5).replace(' Plan', '')
      } else if (currentPlan && line.startsWith('**Badge:**')) {
        currentPlan.badge = line.substring(10).trim()
      } else if (currentPlan && line.startsWith('**Price:**')) {
        currentPlan.price = line.substring(10).trim()
      } else if (currentPlan && line.startsWith('**Caption:**')) {
        currentPlan.priceCaption = line.substring(12).trim()
      } else if (currentPlan && line.startsWith('**Description:**')) {
        currentPlan.description = line.substring(16).trim()
      } else if (currentPlan && line.startsWith('**Stats:**')) {
        currentPlan.stats = line.substring(10).trim()
      } else if (currentPlan && line.startsWith('**CTA:**')) {
        currentPlan.cta = line.substring(8).trim()
      } else if (currentPlan && line.startsWith('**Features:**')) {
        for (let j = i + 1; j < lines.length; j++) {
          const featLine = lines[j].trim()
          if (featLine.startsWith('**') || featLine.startsWith('####') || featLine.startsWith('##')) break
          if (featLine.startsWith('- ')) {
            currentPlan.features.push(featLine.substring(2))
          }
        }
      }
    }
    if (currentPlan) plans.push(currentPlan as LandingContent['pricing']['plans'][number])
  }

  // Parse Contact section
  const contactIndex = lines.findIndex(l => l.trim() === '## Contact Section')
  const contactBadgeIndex = lines.findIndex((l, i) => i > contactIndex && l.trim() === '### Badge')
  const contactTitleIndex = lines.findIndex((l, i) => i > contactIndex && l.trim() === '### Title')
  const contactDescIndex = lines.findIndex((l, i) => i > contactIndex && l.trim() === '### Description')
  const contactCtaIndex = lines.findIndex((l, i) => i > contactIndex && l.trim() === '### CTA Buttons')
  const contactCardIndex = lines.findIndex((l, i) => i > contactIndex && l.trim() === '### Contact Card')

  const contactCtas = contactCtaIndex >= 0 ? extractList(contactCtaIndex + 1) : []

  // Extract contact methods
  let cardTitle = 'Contact'
  let cardDescription = 'Replies within 12 hours'
  let careTeam = 'support@diagnoza.care'
  let partnerships = 'partners@diagnoza.care'
  let secureUploads = 'vault.diagnoza.care/upload'

  if (contactCardIndex >= 0) {
    for (let i = contactCardIndex + 1; i < lines.length; i++) {
      const line = lines[i]?.trim()
      if (!line) continue
      if (line.startsWith('## ')) break
      if (line.startsWith('**Title:**')) cardTitle = line.substring(10).trim()
      if (line.startsWith('**Description:**')) cardDescription = line.substring(16).trim()
      if (line.startsWith('**Care team:**')) careTeam = line.substring(14).trim()
      if (line.startsWith('**Partnerships:**')) partnerships = line.substring(17).trim()
      if (line.startsWith('**Secure uploads:**')) secureUploads = line.substring(19).trim()
    }
  }

  return {
    nav: {
      howItHelps: navItems[0] || '',
      steps: navItems[1] || '',
      stories: navItems[2] || '',
      healthEncyclopedia: navItems[3] || '',
      pricing: navItems[4] || '',
      contact: navItems[5] || '',
    },
    hero: {
      badge: badgeIndex >= 0 ? extractText(badgeIndex + 1) : '',
      headline: headlineIndex >= 0 ? extractText(headlineIndex + 1) : '',
      description: descIndex >= 0 ? extractText(descIndex + 1) : '',
      ctaPrimary: ctaButtons[0] || '',
      ctaSecondary: ctaButtons[1] || '',
      bullets: bulletsIndex >= 0 ? extractList(bulletsIndex + 1) : [],
      medicalKnowledgeTitle: medTitleIndex >= 0 ? extractText(medTitleIndex + 1) : '',
      medicalKnowledgeDescription: medDescIndex >= 0 ? extractText(medDescIndex + 1) : '',
      chatUserMessage: userMsgIndex >= 0 ? extractText(userMsgIndex + 1) : '',
      chatAiResponse: aiRespIndex >= 0 ? extractText(aiRespIndex + 1) : '',
      chatAnxietyEasing: chatTags[0] || 'Anxiety easing',
      chatHotlineReady: chatTags[1] || 'Hotline ready',
      chatMemoryUpdate: memUpdateIndex >= 0 ? extractText(memUpdateIndex + 1) : '',
      stats,
    },
    howItHelps: {
      badge: howBadgeIndex >= 0 ? extractText(howBadgeIndex + 1) : '',
      title: howTitleIndex >= 0 ? extractText(howTitleIndex + 1) : '',
      cards,
    },
    steps: {
      subtitle: stepsSubtitleIndex >= 0 ? extractText(stepsSubtitleIndex + 1) : '',
      title: stepsTitleIndex >= 0 ? extractText(stepsTitleIndex + 1) : '',
      ctaButton: stepsCtaIndex >= 0 ? extractText(stepsCtaIndex + 1) : '',
      items: stepItems,
    },
    dailySupport: {
      badge: dailyBadgeIndex >= 0 ? extractText(dailyBadgeIndex + 1) : '',
      title: dailyTitleIndex >= 0 ? extractText(dailyTitleIndex + 1) : '',
      description: dailyDescIndex >= 0 ? extractText(dailyDescIndex + 1) : '',
      points: dailyPoints,
    },
    trust: {
      title: trustTitleIndex >= 0 ? extractText(trustTitleIndex + 1) : '',
      description: trustDescIndex >= 0 ? extractText(trustDescIndex + 1) : '',
      points: trustPointsIndex >= 0 ? extractList(trustPointsIndex + 1) : [],
    },
    stories: {
      title: storiesTitleIndex >= 0 ? extractText(storiesTitleIndex + 1) : '',
      testimonials,
    },
    pricing: {
      badge: pricingBadgeIndex >= 0 ? extractText(pricingBadgeIndex + 1) : '',
      title: pricingTitleIndex >= 0 ? extractText(pricingTitleIndex + 1) : '',
      plans,
    },
    contact: {
      badge: contactBadgeIndex >= 0 ? extractText(contactBadgeIndex + 1) : '',
      title: contactTitleIndex >= 0 ? extractText(contactTitleIndex + 1) : '',
      description: contactDescIndex >= 0 ? extractText(contactDescIndex + 1) : '',
      ctaPrimary: contactCtas[0] || '',
      ctaSecondary: contactCtas[1] || '',
      cardTitle,
      cardDescription,
      careTeam,
      partnerships,
      secureUploads,
    },
  }
}

// Eager load ALL landing page markdown files at module load time
const modules = import.meta.glob('/src/content/landing/**/*.md', {
  eager: true,
  query: '?raw',
  import: 'default'
})

// Build lookup maps at module initialization
const landingMap = new Map<string, ParsedLanding>()

// Initialize maps
console.log('🚀 Loading landing pages...')
for (const path in modules) {
  try {
    const markdown = modules[path] as string
    const { metadata, content } = parseFrontmatter(markdown)

    // Extract language from path: /src/content/landing/{language}/{filename}.md
    const pathParts = path.split('/')
    const language = pathParts[4]
    const filename = pathParts[5]?.replace('.md', '') || ''

    if (!language || !filename) {
      console.warn(`Skipping file with invalid path: ${path}`)
      continue
    }

    const slugFromMetadata = asString(metadata.slug)
    const slug = slugFromMetadata || filename
    const mapKey = `${language}:${slug}`

    const htmlContent = markdownToHtml(content)
    const parsedContent = parseMarkdownContent(content)

    landingMap.set(mapKey, {
      metadata: {
        title: asString(metadata.title, 'Diagnoza.care'),
        description: asString(metadata.description),
        language: asString(metadata.language, language) || language,
        slug,
      },
      content: parsedContent,
      rawContent: content,
      htmlContent,
    })
  } catch (error) {
    console.error(`Error loading landing page from ${path}:`, error)
  }
}

console.log(`✅ Loaded ${landingMap.size} landing page(s)`)

// Get landing page by language
export function getLandingPage(language: string = 'en'): ParsedLanding | null {
  const mapKey = `${language}:home`
  const landing = landingMap.get(mapKey)

  if (!landing) {
    console.error(`Landing page not found for language: ${language}`)
    return null
  }

  return landing
}
